import { useRef, useState, useEffect } from 'react';

import Icon from '@klv/atoms/icon';
import useIsHydrated from '@klv/hooks/useIsHydrated';
import useOutsideClick from '@klv/hooks/useOutsideClick';
import { UniversalMenuItem } from '@klv/molecules/universal-menu-item';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/languageSelector.module.scss';

import type { TBackgroundColor } from '@klv/organisms/universal-footer/types';
import type { ILanguageSelector } from './types';

const countryNameOverrides = {
  'UK': 'United Kingdom',
  // Korean
  '대한민국': '한국',
};

const LanguageName = ({ className, countryName, languageCode, backgroundColor }: { className?: string; countryName: string; languageCode: string; backgroundColor?: TBackgroundColor; }) => (
  <span className={className}>
    {countryName} - <span className={generateClassName([styles.languageCountry, backgroundColor && styles[backgroundColor]])}>{languageCode}</span>
  </span>
);

const generateLanguageDisplayObject = ({ name, isoCode }: { name: string; isoCode: string; }) => {
  const langCode = isoCode.toUpperCase();
  let countryNativeName = name.match(/\(([^)]+)\)/)?.[1]?.trim() || name;

  countryNativeName = countryNameOverrides[countryNativeName as keyof typeof countryNameOverrides] || countryNativeName;

  return {
    countryNativeName,
    languageCode: langCode,
  };
};

const generateLinkList = ({
  currentLanguage,
  sites,
  translations,
}: ILanguageSelector) => sites.map((site) => {
  const { countryNativeName, languageCode } = generateLanguageDisplayObject({ name: site.nativeName, isoCode: site.isoCode });
  const translation = translations.find(({ hreflang }) => hreflang === site.hreflang);

  if (translation) {
    return ({
      countryName: countryNativeName,
      languageCode,
      current: translation.hreflang === currentLanguage || translation.hreflang === currentLanguage?.split('-')[0],
      fallback: false,
      href: translation.url,
      hrefLang: translation.hreflang,
    });
  }

  return ({
    countryName: countryNativeName,
    languageCode,
    current: site.hreflang === currentLanguage || site.hreflang === currentLanguage?.split('-')[0],
    fallback: true,
    href: site.path,
    hrefLang: site.hreflang,
  });
}).sort((a, _b) => (a.current ? -1 : 0));

export const LanguageSelector = ({
  className,
  currentLanguage = 'en-us',
  sites,
  translations,
  backgroundColor = 'cotton',
  isUtilityNavigation = false,
  isDrawer = false,
  ...props
}: ILanguageSelector) => {
  const isHydrated = useIsHydrated();
  const linkList = generateLinkList({ currentLanguage, sites, translations });
  const currentLink = linkList.find(({ current }) => current);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => {
    setOpen(false);
  });

  useEffect(() => {
    if (!isUtilityNavigation) return;

    const handleScroll = () => {
      if (open) {
        const rect = ref.current?.getBoundingClientRect();
        if (rect && rect.top < 0) {
          setOpen(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUtilityNavigation, open]);

  if (!currentLink || sites.length < 2 || (isUtilityNavigation && !isHydrated)) {
    return null;
  }
  
  return (
    <div className={generateClassName([className, styles.languageSelector, backgroundColor && styles[backgroundColor], open && styles.open, isUtilityNavigation && styles.utilityNavigation, isDrawer && styles.drawer])} {...props} ref={ref}>
      <UniversalMenuItem as='button' className={styles.currentLanguage} onClick={() => setOpen((value) => !value)}>
        <Icon color='currentColor' iconName='Globe' size={1.6} />
        <LanguageName className={generateClassName([styles.currentLanguageName, isDrawer && styles.drawer])} backgroundColor={backgroundColor} countryName={currentLink.countryName} languageCode={currentLink.languageCode} />
        <Icon color='currentColor' className={styles.chevron} iconName='ChevronDown' size={0.3} />
      </UniversalMenuItem>
      <nav className={generateClassName([styles.languageMenu, isUtilityNavigation && styles.utilityNavigation, isDrawer && styles.drawer])}>
        <ul className={styles.languageList}>
          {linkList.map(({ countryName, languageCode, current, fallback, href, hrefLang }) => (
            <li key={hrefLang} className={styles.listItem}>
              <UniversalMenuItem
                aria-current={current || undefined}
                className={generateClassName([styles.link, backgroundColor && styles[backgroundColor]])}
                rel={fallback ? undefined : 'alternate'}
                href={href}
                hrefLang={hrefLang}
                lang={hrefLang}
              >
                <LanguageName countryName={countryName} languageCode={languageCode} />
                {current && (<Icon aria-label='(current language)' color='currentColor' iconName='Check' size={1.4} />)}
              </UniversalMenuItem>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
